import Modal from "@/components/common/modal";
import { useUIStore } from "../store/uiStore";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
} from "@elaniintc/goes-design-system";
import { useWorkflowStore } from "../store/workflowStore";
import { useMemo } from "react";
import { type NodeData } from "../types/workflow";
import { getNodeContent } from "../components/node/content";

const NodeValidationErrorsModal = () => {
  const { nodes } = useWorkflowStore();
  const { selectedErrors, setModalState, setSelectedErrors } = useUIStore();

  const nodeAffected = useMemo(() => {
    if (!selectedErrors) return null;
    const errorNodeId = selectedErrors[0]?.node;
    if (!errorNodeId) return null;
    const node = nodes.find((n) => n.id === errorNodeId);
    if (!node) return null;
    const data = node.data as unknown as NodeData;
    return {
      id: node.id,
      type: data.type,
      label: data.displayName,
      content: getNodeContent(data.type),
    };
  }, [nodes, selectedErrors]);

  const setErrors = useMemo(() => {
    if (!selectedErrors) return [];
    const uniqueErrors = selectedErrors.reduce(
      (acc, current) => {
        const key =
          current.type === "INVALID_INPUT_VALUE"
            ? `${current.type}::${current.message}`
            : current.type;
        const x = acc.find((item) => {
          const itemKey =
            item.type === "INVALID_INPUT_VALUE"
              ? `${item.type}::${item.message}`
              : item.type;
          return itemKey === key;
        });
        if (!x) {
          return acc.concat([current]);
        } else {
          return acc;
        }
      },
      [] as Array<{ node: string; message: string; type: string }>,
    );
    return uniqueErrors;
  }, [selectedErrors]);

  if (!nodeAffected || !selectedErrors) return null;

  return (
    <Modal
      isOpen={true}
      onClose={() => {
        setModalState("idle");
        setSelectedErrors(undefined);
      }}
      title="Detalle de error"
      description="A continuación se detallan los errores presentes en el nodo."
      modalContentClassName="w-full max-w-[var(--size-horizontal-sm)]"
    >
      <div className="pt-8 flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <div className="w-full border border-neutral-quiet bg-neutral-object px-3 py-2 rounded-lg flex gap-2">
            <Avatar shape="circle" size="2xs">
              <AvatarFallback
                shape="circle"
                type="icon"
                color={nodeAffected.content.labelType}
              >
                <Icon
                  type={nodeAffected.content.icon.type}
                  name={nodeAffected.content.icon.name}
                />
              </AvatarFallback>
            </Avatar>
            <p className="text-body">{nodeAffected.label}</p>
          </div>
          <ul className="px-3 py-0.5 flex flex-col gap-2">
            {setErrors.map((error, index) => {
              const icon = getErrorIcon(error.type);
              return (
                <li
                  key={`node-error-${nodeAffected.id}-${index}`}
                  className="text-danger leading-3xl flex items-center gap-3"
                >
                  <Avatar shape="circle" size="2xs">
                    <AvatarFallback shape="circle" type="icon" color="danger">
                      <Icon type={icon.type} name={icon.name} />
                    </AvatarFallback>
                  </Avatar>
                  {error.type === "INVALID_INPUT_VALUE"
                    ? error.message
                    : getErrorText(error.type)}
                </li>
              );
            })}
          </ul>
        </div>
        <Button
          variant="tertiary"
          className="w-full"
          onClick={() => {
            setModalState("idle");
            setSelectedErrors(undefined);
          }}
        >
          Aceptar
        </Button>
      </div>
    </Modal>
  );
};

export default NodeValidationErrorsModal;

const getErrorText = (error: string) => {
  switch (error) {
    case "MUST_HAVE_NODE_EDGE":
      return "No tiene conexiones entrantes.";
    case "AT_LEAST_ONE_CONDITION_TYPE":
    case "MUST_HAVE_REQUIRED_INPUTS":
      return "No se ha configurado su comportamiento.";
    case "MUST_HAVE_TARGET_ON_CONNECTION":
      return "Todas las salidas deben estar conectadas a otro nodo.";
    case "MUST_HAVE_TARGET_FROM_SAME_FLOW":
      return "Todas las conexiones deben ser hacia nodos dentro del mismo flujo.";
    default:
      return "Error desconocido.";
  }
};

const getErrorIcon = (error: string) => {
  switch (error) {
    case "MUST_HAVE_NODE_EDGE":
    case "MUST_HAVE_TARGET_ON_CONNECTION":
      return { type: "fas", name: "link-slash" };
    case "AT_LEAST_ONE_CONDITION_TYPE":
    case "MUST_HAVE_REQUIRED_INPUTS":
      return { type: "fas", name: "edit" };
    case "MUST_HAVE_TARGET_FROM_SAME_FLOW":
      return { type: "fas", name: "sitemap" };
    case "INVALID_INPUT_VALUE":
      return { type: "fas", name: "exclamation-triangle" };
    default:
      return { type: "fas", name: "exclamation-triangle" };
  }
};
